import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import L, { LatLngExpression } from 'leaflet';
import { ChargingLocation } from '../../types';
import { BRAND_COLORS, MAP_CONFIG } from '../../constants';
import { LocationDisplayStatus } from '../../store/locationStatusStore';
import { useDarkMode } from '../contexts/DarkModeContext';

interface MapViewProps {
  locations: ChargingLocation[];
  onLocationClick: (location: ChargingLocation) => void;
  center: LatLngExpression;
  onMapMove?: (center: { latitude: number; longitude: number }) => void;
  /** Get display status for a location (available/occupied/offline). If not provided, falls back to location.available */
  getDisplayStatus?: (location: ChargingLocation) => LocationDisplayStatus;
}

export interface MapViewRef {
  panTo: (center: [number, number]) => void;
}

export const MapView = forwardRef<MapViewRef, MapViewProps>(
  ({ locations, onLocationClick, center, onMapMove, getDisplayStatus }, ref) => {
    const mapRef = useRef<L.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const markersRef = useRef<L.Marker[]>([]);
    const { isDarkMode } = useDarkMode();

    // Expose panTo method via ref
    useImperativeHandle(ref, () => ({
      panTo: (newCenter: [number, number]) => {
        if (mapRef.current) {
          mapRef.current.setView(newCenter, mapRef.current.getZoom(), {
            animate: true,
            duration: 0.5,
          });
        }
      },
    }));

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Initialize map
    const map = L.map(mapContainerRef.current).setView(center as L.LatLngExpression, MAP_CONFIG.defaultZoom);
    mapRef.current = map;

    // Add tile layer with better styling
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: MAP_CONFIG.maxZoom,
    }).addTo(map);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Update map center when center prop changes
  useEffect(() => {
    if (mapRef.current && center) {
      mapRef.current.setView(center as L.LatLngExpression, mapRef.current.getZoom(), {
        animate: true,
        duration: 0.5,
      });
    }
  }, [center]);

  // Handle map move events
  useEffect(() => {
    if (!mapRef.current || !onMapMove) return;

    const handleMoveEnd = () => {
      if (mapRef.current) {
        const center = mapRef.current.getCenter();
        onMapMove({
          latitude: center.lat,
          longitude: center.lng,
        });
      }
    };

    mapRef.current.on('moveend', handleMoveEnd);

    return () => {
      if (mapRef.current) {
        mapRef.current.off('moveend', handleMoveEnd);
      }
    };
  }, [onMapMove]);

  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Color mapping for three-state markers
    const statusColors: Record<LocationDisplayStatus, string> = {
      available: BRAND_COLORS.primary,  // Green - available for charging
      occupied: '#f59e0b',               // Orange/Amber - online but occupied
      offline: '#9ca3af',                // Gray - offline
    };

    // Create custom icon function with status-based color
    const createIcon = (status: LocationDisplayStatus) => {
      const color = statusColors[status];
      const svgIcon = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8" fill="${color}" stroke="white" stroke-width="2"/>
        </svg>
      `;

      return L.icon({
        iconUrl: `data:image/svg+xml;base64,${btoa(svgIcon)}`,
        iconSize: MAP_CONFIG.markerIconSize,
        iconAnchor: MAP_CONFIG.markerIconAnchor,
        popupAnchor: [0, 0],
      });
    };

    // Add markers for each location
    locations.forEach((location) => {
      // Get display status - use provided getter or fall back to location.available
      const displayStatus: LocationDisplayStatus = getDisplayStatus
        ? getDisplayStatus(location)
        : (location.available ? 'available' : 'occupied');

      const marker = L.marker(location.position as L.LatLngExpression, {
        icon: createIcon(displayStatus),
      }).addTo(mapRef.current!);

      // Add click handler
      marker.on('click', () => {
        onLocationClick(location);
      });

      markersRef.current.push(marker);
    });
  }, [locations, onLocationClick, getDisplayStatus]);

  return (
    <div
      ref={mapContainerRef}
      className={`h-full w-full rounded-t-3xl overflow-hidden shadow-inner ${isDarkMode ? 'dark-mode-tiles' : ''}`}
      style={{ zIndex: 0 }}
    />
  );
});

MapView.displayName = 'MapView';